/**
 * Custom Patch Request Form - Formspree Handler
 * Handles form validation, multi-file upload, and submission to Formspree
 */

/**
 * Required-by date rule (rush rule, version 1).
 *
 * Counted in whole calendar days from the day the form is submitted, on the
 * Pacific calendar (the shop's operating calendar, and the calendar the intake
 * app uses when it recomputes the same rule on receipt):
 *   - STANDARD_MIN_DAYS or more: standard production, no fee
 *   - RUSH_MIN_DAYS to STANDARD_MIN_DAYS - 1: rush, RUSH_FEE, customer must agree
 *   - fewer than RUSH_MIN_DAYS: refused; the form offers the next accepted dates
 *
 * The constants and arithmetic are the same as the intake app's rule module and
 * must change together with it in one versioned release (bump RUSH_RULE_VERSION).
 * Customers never see the day counts, only dates written in words.
 */
const RushRule = Object.freeze({
  RUSH_RULE_VERSION: '1',
  ART_APPROVAL_DAYS: 2,
  STANDARD_PRODUCTION_DAYS: 21,
  FASTEST_PRODUCTION_DAYS: 7,
  get STANDARD_MIN_DAYS() { return this.ART_APPROVAL_DAYS + this.STANDARD_PRODUCTION_DAYS; },
  get RUSH_MIN_DAYS() { return this.ART_APPROVAL_DAYS + this.FASTEST_PRODUCTION_DAYS; },
  RUSH_FEE: '200.00',
  NO_FEE: '0.00',
  RUSH_FEE_LABEL: '$200',
  TIME_ZONE: 'America/Los_Angeles',
  MS_PER_DAY: 86400000,

  pacificCalendarDate(instant) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: this.TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(new Date(instant === undefined ? Date.now() : instant));
    const pick = (type) => (parts.find((part) => part.type === type) || {}).value || '';
    return `${pick('year')}-${pick('month')}-${pick('day')}`;
  },

  isYmd(value) {
    return /^\d{4}-\d{2}-\d{2}$/.test(value || '') && Number.isFinite(Date.parse(`${value}T00:00:00Z`));
  },

  addDays(ymd, days) {
    return new Date(Date.parse(`${ymd}T00:00:00Z`) + days * this.MS_PER_DAY).toISOString().slice(0, 10);
  },

  daysBetween(fromYmd, toYmd) {
    return Math.round((Date.parse(`${toYmd}T00:00:00Z`) - Date.parse(`${fromYmd}T00:00:00Z`)) / this.MS_PER_DAY);
  },

  classifyRequestedDate(requestedDate, submittedOn) {
    const days = this.daysBetween(submittedOn, requestedDate);
    const tier = days >= this.STANDARD_MIN_DAYS ? 'standard' : days >= this.RUSH_MIN_DAYS ? 'rush' : 'refused';
    return {
      ruleVersion: this.RUSH_RULE_VERSION,
      submittedOn,
      requestedDate,
      days,
      tier,
      fee: tier === 'rush' ? this.RUSH_FEE : this.NO_FEE,
      standardDate: this.addDays(submittedOn, this.STANDARD_MIN_DAYS),
      earliestDate: this.addDays(submittedOn, this.RUSH_MIN_DAYS)
    };
  },

  /** "September 30, 2026" - the only way a date is ever shown to the customer. */
  formatDateInWords(ymd) {
    return new Date(`${ymd}T00:00:00Z`).toLocaleDateString('en-US', {
      timeZone: 'UTC',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  }
});

const RushCopy_firstComeSentence = 'Rush dates are first come, first served: the sooner your payment is in, the sooner our artist starts and the more certain your date becomes.';

const RushCopy = Object.freeze({
  helperDefault: 'Enter exact date needed.',
  helperStandard: 'Standard production',
  helperRush: `Rush requested, +${RushRule.RUSH_FEE_LABEL}`,
  eyebrow: 'Required by date',
  title: 'About your date',
  // Bodies are arrays of text runs; { strong: '...' } runs render in bold.
  rushBody: (standardDate, requestedDate) => [
    'Our standard timeline delivers on or after ', { strong: standardDate }, '. You asked for ', { strong: requestedDate },
    ', which is sooner. You can keep your date with a ', { strong: `${RushRule.RUSH_FEE_LABEL} rush fee` },
    ', or switch to the standard date at no charge. ', RushCopy_firstComeSentence,
    ' The rush fee covers faster production only. Expedited shipping is added on top, and we confirm that cost in your quote. Nothing is charged today.'
  ],
  // Buttons are two short lines (action, then the fee) so no label ever wraps, even on narrow phones.
  rushKeep: { main: 'Keep my date', sub: `adds a ${RushRule.RUSH_FEE_LABEL} rush fee` },
  refusedBody: (requestedDate, earliestDate, standardDate) => [
    "We can't deliver by ", { strong: requestedDate }, '. The soonest we can do is ', { strong: earliestDate },
    ' with a ', { strong: `${RushRule.RUSH_FEE_LABEL} rush fee` }, ', or ', { strong: standardDate },
    ' at no charge. ', RushCopy_firstComeSentence,
    ' The rush fee covers faster production only, and expedited shipping is added on top. Nothing is charged today.'
  ],
  refusedKeep: (earliestDate) => ({ main: `Use ${earliestDate}`, sub: `adds a ${RushRule.RUSH_FEE_LABEL} rush fee` }),
  useStandard: (standardDate) => ({ main: `Use ${standardDate}, or later`, sub: 'no fee' })
});

/**
 * The upload budget of ONE request, from the form service's published limits
 * (Formspree "File uploads", read 2026-09-30): at most 10 files per submission,
 * 100 MB for the entire request, and 30 seconds per request.
 *
 * MAX_TOTAL_BYTES is 95,000,000 bytes of file content: 5,000,000 bytes (5%)
 * under the smaller reading of "100 MB" (100,000,000 bytes). That margin is
 * for the multipart framing, the text fields and the request headers, which
 * measure under 10,000 bytes for a full six-patch set, so it is about 500
 * times what the framing needs.
 *
 * SLOW_WARNING_BYTES is where the form starts warning (it does not block):
 * 30,000,000 bytes need 8 Mbit/s sustained to finish inside 30 seconds
 * (30,000,000 x 8 / 30). 8 Mbit/s is an honest estimate of an ordinary phone
 * uplink; real customer upload timings would settle it.
 */
const UploadBudget = Object.freeze({
  MAX_FILES: 10,
  MAX_TOTAL_BYTES: 95 * 1000 * 1000,
  SLOW_WARNING_BYTES: 30 * 1000 * 1000,
  DEADLINE_SECONDS: 30
});

/** A name tag's minimum inside a multi-patch order: the name-tag table's, whatever the material. */
const NAME_TAG_MINIMUM = 25;

class RequestFormUpload {
  constructor(formElement) {
    this.form = formElement;
    this.fileInputs = Array.from(this.form.querySelectorAll('input[type="file"]'));
    this.submitButton = this.form.querySelector('button[type="submit"], input[type="submit"]');
    this.patchTypeField = this.form.querySelector('[name="patch_type"]');
    this.quantityField = this.form.querySelector('[name="quantity"]');
    this.quantityHint = this.form.querySelector('[data-quantity-hint]');
    this.patchSizeField = this.form.querySelector('[name="patch_size"]');
    this.customSizeFieldWrap = this.form.querySelector('[data-custom-size-field]');
    this.customSizeField = this.form.querySelector('[name="custom_patch_size"]');
    // Leather finish (only the custom patches form carries this marker)
    this.leatherFinishField = this.form.querySelector('[data-leather-finish-field]');
    this.leatherFinishInputs = this.leatherFinishField
      ? Array.from(this.leatherFinishField.querySelectorAll('input[name="leather_finish"]'))
      : [];
    this.leatherFinishHint = this.leatherFinishField ? this.leatherFinishField.querySelector('[data-leather-finish-hint]') : null;
    this.leatherFinishHintText = this.leatherFinishHint ? this.leatherFinishHint.textContent : '';
    this.defaultQuantityMinimum = this.getConfiguredMinimumQuantity();
    this.allowedFormHostSuffix = 'formspree.io';
    this.selectedFilesByInput = new WeakMap();
    this.successCountdownInterval = null;
    this.successCloseTimeout = null;
    // Required-by date rule (only the custom patches form carries these markers)
    this.dateField = this.form.querySelector('[data-date-input]');
    this.dateHelper = this.form.querySelector('[data-date-helper]');
    this.rushFields = {};
    this.form.querySelectorAll('[data-rush-field]').forEach((input) => {
      this.rushFields[input.dataset.rushField] = input;
    });
    this.rushDecision = null;
    this.dateModal = null;
    
    this.init();
  }

  init() {
    // Handle form submission
    this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    
    // Handle file input change
    this.fileInputs.forEach((fileInput) => {
      this.selectedFilesByInput.set(fileInput, []);
      fileInput.addEventListener('change', (e) => this.handleFileSelect(e));
    });
    this.initFileUploadGroups();

    if (this.patchTypeField) {
      this.patchTypeField.addEventListener('change', () => {
        this.updateQuantityGuidance();
        this.updateLeatherFinishGuidance();
      });
      this.updateQuantityGuidance();
    } else if (this.quantityField && this.defaultQuantityMinimum > 1) {
      this.updateStaticQuantityGuidance();
    }

    if (this.quantityField) {
      this.quantityField.addEventListener('blur', () => this.enforceQuantityMinimum());
      this.quantityField.addEventListener('change', () => this.enforceQuantityMinimum());
    }

    if (this.patchSizeField) {
      this.patchSizeField.addEventListener('change', () => this.updatePatchSizeGuidance());
      this.updatePatchSizeGuidance();
    }

    if (this.dateField) {
      this.initRushRule();
    }

    if (this.leatherFinishInputs.length) {
      this.leatherFinishInputs.forEach((input) => {
        input.addEventListener('change', () => this.updateLeatherFinishValidity());
        // The browser's bubble fades; the line under the choices keeps saying what is missing.
        input.addEventListener('invalid', () => this.showLeatherFinishProblem(true));
      });
    }

    // Matching set (only the custom patches form carries the question)
    this.initMatchingSet();
    this.updateLeatherFinishGuidance();

    // A page restored from the back-forward cache, or by the browser's own form
    // restore, can hold a patch type no change event announced; the fields that
    // depend on it are derived again so a locked quantity box is never left behind.
    window.addEventListener('pageshow', () => this.syncDependentFields());
  }

  // ---------------------------------------------------------------------------
  // Matching set: several patches for the same unit on ONE order
  // (Jack via the captain 2026-09-29, decided 2026-09-30). "No" keeps the
  // single-patch fields exactly as they are. "Yes" disables them and shows one
  // repeated patch card (up to six) whose inputs are named patch_<n>_*, plus the
  // hidden matching_set / set_version markers. Whichever block is not in use is
  // disabled, so it is neither validated nor posted.
  // ---------------------------------------------------------------------------

  initMatchingSet() {
    this.setQuestion = this.form.querySelector('[data-set-question]');
    if (!this.setQuestion) return;
    this.setSingle = this.form.querySelector('[data-set-single]');
    this.setBlock = this.form.querySelector('[data-set-block]');
    this.setCards = this.form.querySelector('[data-set-cards]');
    this.setTemplate = this.form.querySelector('[data-set-card-template]');
    this.setAddButton = this.form.querySelector('[data-set-add]');
    this.setCount = this.form.querySelector('[data-set-count]');
    this.setOneOrder = this.form.querySelector('[data-set-one-order]');
    this.setOnlyElements = Array.from(this.form.querySelectorAll('[data-set-only]'));
    this.setModeInputs = Array.from(this.form.querySelectorAll('[data-set-mode]'));
    this.setMarkers = Array.from(this.form.querySelectorAll('[data-set-marker]'));
    this.setMax = Number(this.setBlock.dataset.setMax || 6);
    if (!this.setSingle || !this.setBlock || !this.setCards || !this.setTemplate || !this.setAddButton) return;

    this.setModeInputs.forEach((input) => input.addEventListener('change', () => this.applySetMode()));
    this.setAddButton.addEventListener('click', () => this.addSetCard({ foldOthers: true, focus: true }));
    this.form.addEventListener('reset', () => {
      setTimeout(() => this.resetMatchingSet(), 0);
    });

    if (!this.getSetCards().length) this.addSetCard({ foldOthers: false, focus: false });
    this.applySetMode();
  }

  isSetMode() {
    // Only the custom patches form carries the question; every other form is single.
    if (!Array.isArray(this.setModeInputs) || !this.setBlock) return false;
    const checked = this.setModeInputs.find((input) => input.checked);
    return Boolean(checked && checked.value === 'yes');
  }

  getSetCards() {
    return this.setCards ? Array.from(this.setCards.querySelectorAll('[data-set-card]')) : [];
  }

  applySetMode() {
    if (!this.setBlock) return;
    const setMode = this.isSetMode();
    this.setSingle.hidden = setMode;
    this.setSingle.querySelectorAll('input, select, textarea').forEach((element) => {
      element.disabled = setMode;
    });
    this.setBlock.hidden = !setMode;
    this.setBlock.querySelectorAll('input, select, textarea').forEach((element) => {
      element.disabled = !setMode;
    });
    this.setMarkers.forEach((input) => {
      input.disabled = !setMode;
    });
    this.setOnlyElements.forEach((element) => {
      element.hidden = !setMode;
    });
    if (!setMode) {
      // The single quantity field stays locked until a patch type is chosen.
      this.updateQuantityGuidance();
      // The leather finish stays locked unless the patch type is Leather.
      this.updateLeatherFinishGuidance();
    }
    this.refreshUploadBudgetState();
  }

  resetMatchingSet() {
    if (!this.setCards) return;
    this.getSetCards().forEach((card) => card.remove());
    this.addSetCard({ foldOthers: false, focus: false });
    this.applySetMode();
  }

  /**
   * File slots this multi-patch order needs: every card's artwork, a name tag's
   * name list, and a name tag's reference photo once one is chosen. Required
   * files count before they are chosen, so the order can never be built past
   * what one request can upload. `assumeNameTag` / `assumeChosen` answer "what if".
   */
  setFileSlotsUsed({ assumeNameTag = null, assumeChosen = null } = {}) {
    return this.getSetCards().reduce((slots, card) => {
      const kind = this.setKindOf(card);
      const nameTag = card === assumeNameTag || Boolean(kind && kind.value === 'nametag');
      const reference = card.querySelector('[data-set-reference-photo]');
      const referenceChosen = Boolean(nameTag && reference && (reference === assumeChosen || this.filesOf(reference).length));
      return slots + 1 + (nameTag ? 1 : 0) + (referenceChosen ? 1 : 0);
    }, 0);
  }

  /** A new card needs at least its artwork file. */
  setCanAddCard() {
    return this.getSetCards().length < this.setMax && this.setFileSlotsUsed() + 1 <= UploadBudget.MAX_FILES;
  }

  addSetCard({ foldOthers, focus }) {
    const cards = this.getSetCards();
    if (cards.length >= this.setMax) return null;
    if (cards.length && !this.setCanAddCard()) {
      this.showError(`One request can upload up to ${UploadBudget.MAX_FILES} files, and this order already needs ${this.setFileSlotsUsed()}. Send any further patches as a second request.`);
      this.refreshUploadBudgetState();
      return null;
    }
    if (foldOthers && !this.foldOpenSetCards()) return null;
    const index = cards.length + 1;
    const html = this.setTemplate.innerHTML.replace(/__N__/g, String(index));
    this.setCards.insertAdjacentHTML('beforeend', html);
    const card = this.getSetCards()[index - 1];
    this.bindSetCard(card);
    this.refreshUploadBudgetState();
    if (focus) {
      const first = card.querySelector('[data-set-kind]');
      if (first) first.focus();
    }
    return card;
  }

  bindSetCard(card) {
    card.querySelectorAll('[data-set-kind]').forEach((radio) => {
      radio.addEventListener('change', () => this.onSetKindChosen(card));
    });
    const type = card.querySelector('[data-set-type]');
    if (type) type.addEventListener('change', () => this.updateSetCardQuantityGuidance(card));
    const quantity = card.querySelector('[data-set-quantity]');
    if (quantity) {
      quantity.addEventListener('blur', () => this.enforceSetCardQuantityMinimum(card));
      quantity.addEventListener('change', () => this.enforceSetCardQuantityMinimum(card));
    }
    const sizeInput = card.querySelector('[data-set-size-input]');
    if (sizeInput) sizeInput.addEventListener('input', () => { sizeInput.dataset.touched = sizeInput.value ? '1' : ''; });
    card.querySelectorAll('input[type="file"]').forEach((input) => this.registerFileInput(input));
    const remove = card.querySelector('[data-set-card-remove]');
    if (remove) remove.addEventListener('click', () => this.removeSetCard(card));
    const edit = card.querySelector('[data-set-card-edit]');
    if (edit) edit.addEventListener('click', () => this.unfoldSetCard(card));
    this.onSetKindChange(card);
    this.updateSetCardQuantityGuidance(card);
    card.dataset.setKind = '';
  }

  registerFileInput(input) {
    if (this.fileInputs.includes(input)) return;
    this.fileInputs.push(input);
    this.selectedFilesByInput.set(input, []);
    input.addEventListener('change', (event) => this.handleFileSelect(event));
  }

  removeSetCard(card) {
    const cards = this.getSetCards();
    if (cards.length <= 1) return;
    card.querySelectorAll('input[type="file"]').forEach((input) => {
      this.fileInputs = this.fileInputs.filter((candidate) => candidate !== input);
    });
    card.remove();
    this.renumberSetCards();
    this.refreshUploadBudgetState();
  }

  renumberSetCards() {
    this.getSetCards().forEach((card, position) => {
      const index = position + 1;
      card.dataset.setIndex = String(index);
      card.querySelectorAll('[name]').forEach((element) => {
        element.name = element.name.replace(/^patch_\d+_/, `patch_${index}_`);
      });
      card.querySelectorAll('[data-set-card-number], [data-set-card-n]').forEach((element) => {
        element.textContent = String(index);
      });
      const remove = card.querySelector('[data-set-card-remove]');
      if (remove) remove.setAttribute('aria-label', `Remove patch ${index}`);
    });
  }

  setKindOf(card) {
    const checked = card.querySelector('[data-set-kind]:checked');
    return checked || null;
  }

  /** The customer picked a kind: a name tag needs a second file, so check the budget first. */
  onSetKindChosen(card) {
    const kind = this.setKindOf(card);
    const previous = card.dataset.setKind || '';
    if (kind && kind.value === 'nametag' && previous !== 'nametag' && this.setFileSlotsUsed() > UploadBudget.MAX_FILES) {
      // Put the choice back: the earlier kind, or nothing chosen yet.
      card.querySelectorAll('[data-set-kind]').forEach((radio) => {
        radio.checked = Boolean(previous) && radio.value === previous;
      });
      this.showError(`Name tags need two files each, the artwork and the name list, and this order is at the ${UploadBudget.MAX_FILES}-file limit one request can upload. Remove a patch first, or send these name tags as a second request.`);
    }
    const settled = this.setKindOf(card);
    card.dataset.setKind = settled ? settled.value : '';
    this.onSetKindChange(card);
    this.updateSetCardQuantityGuidance(card);
    this.enforceSetCardQuantityMinimum(card);
    this.refreshUploadBudgetState();
  }

  /** The card's minimum: 25 for a name tag whatever its material, else the patch type's. */
  getSetCardMinimum(card) {
    const kind = this.setKindOf(card);
    if (kind && kind.value === 'nametag') return { minimum: NAME_TAG_MINIMUM, label: 'name tags' };
    const type = card.querySelector('[data-set-type]');
    if (!type || !type.value) return null;
    return { minimum: this.getMinimumForPatchType(type.value), label: type.value };
  }

  onSetKindChange(card) {
    const kind = this.setKindOf(card);
    const label = kind ? kind.dataset.setLabel : '';
    const kindBadge = card.querySelector('[data-set-card-kind]');
    if (kindBadge) kindBadge.textContent = label ? ` - ${label}` : '';
    const sizeInput = card.querySelector('[data-set-size-input]');
    const sizeHint = card.querySelector('[data-set-size-hint]');
    if (sizeInput && kind) {
      const previousDefault = sizeInput.dataset.defaultSize || '';
      const nextDefault = kind.dataset.setSize || '';
      // Pre-fill the usual size for this kind unless the customer typed their own.
      if (!sizeInput.dataset.touched || sizeInput.value === previousDefault) {
        sizeInput.value = nextDefault;
        sizeInput.dataset.touched = '';
      }
      sizeInput.dataset.defaultSize = nextDefault;
      if (sizeHint) sizeHint.textContent = kind.dataset.setHint || '';
    }
    const nametag = card.querySelector('[data-set-nametag]');
    if (nametag) {
      const isNameTag = Boolean(kind && kind.value === 'nametag');
      nametag.hidden = !isNameTag;
      nametag.querySelectorAll('input').forEach((input) => {
        input.disabled = !isNameTag || !this.isSetMode();
      });
      const names = nametag.querySelector('[data-set-names-file]');
      if (names) names.required = isNameTag;
    }
  }

  syncSetCards() {
    if (!this.setCards) return;
    const cards = this.getSetCards();
    const setMode = this.isSetMode();
    cards.forEach((card) => {
      // The name-tag extras follow the card's kind even after a mode switch
      // enabled or disabled every input in the block.
      this.onSetKindChange(card);
      const remove = card.querySelector('[data-set-card-remove]');
      if (remove) remove.hidden = cards.length <= 1;
    });
    const slots = this.setFileSlotsUsed();
    const fileBudgetFull = cards.length < this.setMax && slots + 1 > UploadBudget.MAX_FILES;
    if (this.setAddButton) this.setAddButton.hidden = cards.length >= this.setMax || fileBudgetFull;
    if (this.setCount) {
      this.setCount.textContent = cards.length >= this.setMax
        ? `${cards.length} of ${this.setMax} added. That is the most one order can carry.`
        : fileBudgetFull
          ? `${cards.length} of ${this.setMax} added. This order already needs ${slots} files, and one request can upload up to ${UploadBudget.MAX_FILES}. Send any further patches as a second request.`
          : `${cards.length} of ${this.setMax} added. Each one gets the same card.`;
    }
    if (this.setOneOrder) {
      this.setOneOrder.textContent = cards.length > 1
        ? `One rush fee and one shipping charge cover all ${cards.length} patches, however many you add.`
        : 'One rush fee and one shipping charge cover every patch you add.';
    }
    if (!setMode) return;
  }

  updateSetCardQuantityGuidance(card) {
    const quantity = card.querySelector('[data-set-quantity]');
    const hint = card.querySelector('[data-set-quantity-hint]');
    if (!quantity) return;
    const rule = this.getSetCardMinimum(card);
    if (!rule) {
      quantity.placeholder = 'Quantity Depends on Patch Type';
      quantity.min = '1';
      if (hint) hint.textContent = 'Quantity Depends on Patch Type';
      return;
    }
    quantity.min = String(rule.minimum);
    quantity.placeholder = `Min. ${rule.minimum} units`;
    if (hint) hint.textContent = `Minimum order for ${rule.label}: ${rule.minimum} units`;
  }

  /** Raises a too-low quantity to the card's minimum; true when it changed the value. */
  enforceSetCardQuantityMinimum(card) {
    const quantity = card.querySelector('[data-set-quantity]');
    const hint = card.querySelector('[data-set-quantity-hint]');
    const rule = this.getSetCardMinimum(card);
    if (!quantity || !rule) return false;
    const value = Number(quantity.value || 0);
    if (Number.isFinite(value) && value > 0 && value < rule.minimum) {
      quantity.value = String(rule.minimum);
      if (hint) hint.textContent = `Minimum for ${rule.label}: ${rule.minimum}. Quantity adjusted to ${rule.minimum}.`;
      return true;
    }
    return false;
  }

  /** Folds every open, complete card to its one-line summary; false (and the browser's message) when one is incomplete. */
  foldOpenSetCards() {
    const open = this.getSetCards().filter((card) => !card.classList.contains('is-folded'));
    for (const card of open) {
      const inputs = Array.from(card.querySelectorAll('input, select')).filter((element) => !element.disabled);
      const invalid = inputs.find((element) => !element.checkValidity());
      if (invalid) {
        invalid.reportValidity();
        return false;
      }
      this.foldSetCard(card);
    }
    return true;
  }

  foldSetCard(card) {
    const kind = this.setKindOf(card);
    const lettering = (card.querySelector('[data-set-lettering]') || {}).value || '';
    const type = (card.querySelector('[data-set-type]') || {}).value || '';
    const size = (card.querySelector('[data-set-size-input]') || {}).value || '';
    const backing = ((card.querySelector('[data-set-backing]') || {}).value || '').replace(/\s*\(.*\)$/, '');
    const quantity = (card.querySelector('[data-set-quantity]') || {}).value || '';
    const main = card.querySelector('[data-set-card-summary-main]');
    const sub = card.querySelector('[data-set-card-summary-sub]');
    if (main) main.textContent = `${kind ? kind.dataset.setLabel : 'Patch'} - ${lettering}`;
    if (sub) sub.textContent = [type, size, backing, quantity ? `quantity ${quantity}` : ''].filter(Boolean).join(', ');
    card.classList.add('is-folded');
  }

  unfoldSetCard(card) {
    card.classList.remove('is-folded');
    const first = card.querySelector('[data-set-lettering]');
    if (first) first.focus();
  }

  validateSetCards() {
    if (!this.isSetMode()) return true;
    const cards = this.getSetCards();
    if (!cards.length) {
      this.showError('Add at least one patch to the order.');
      return false;
    }
    // A quantity under its minimum is raised and shown, never raised and sent in
    // the same breath: the customer sees the change and submits again.
    const adjusted = cards.filter((card) => this.enforceSetCardQuantityMinimum(card));
    if (adjusted.length) {
      adjusted.forEach((card) => this.unfoldSetCard(card));
      const first = adjusted[0];
      const rule = this.getSetCardMinimum(first);
      this.showError(`Patch ${first.dataset.setIndex}: the minimum for ${rule.label} is ${rule.minimum}, so the quantity was changed to ${rule.minimum}. Check it, then submit again.`);
      return false;
    }
    return true;
  }

  // ---------------------------------------------------------------------------
  // Required-by date rule
  // ---------------------------------------------------------------------------

  initRushRule() {
    this.refreshDateMinimum();
    this.dateField.addEventListener('focus', () => this.refreshDateMinimum());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.refreshDateMinimum();
    });
    window.addEventListener('pageshow', () => this.refreshDateMinimum());

    // The picker is the only way to set a date: a click anywhere in the field
    // (not just the calendar icon) and Enter/Space open it; typing is blocked.
    // showPicker() must run inside the user gesture; browsers without it, or a
    // cross-origin frame, fall back to the native segment behaviour.
    this.dateField.addEventListener('click', () => this.openDatePicker());
    this.dateField.addEventListener('keydown', (event) => {
      const key = event.key;
      if (key === 'Tab' || key === 'Escape') return;
      event.preventDefault();
      if (key === 'Enter' || key === ' ' || key === 'Spacebar') {
        this.openDatePicker();
      }
    });
    this.dateField.addEventListener('change', () => this.classifyDateChoice());

    // form.reset() (after a successful submit) clears the date; the reset event
    // fires before the values are cleared, so finish the bookkeeping afterwards.
    this.form.addEventListener('reset', () => {
      setTimeout(() => this.clearRushState(), 0);
    });

    // The script is deferred and the browser may restore a value, so classify
    // whatever the field already holds.
    this.classifyDateChoice();
  }

  openDatePicker() {
    if (!this.dateField || this.dateField.disabled) return;
    if (typeof this.dateField.showPicker !== 'function') return;
    try {
      this.dateField.showPicker();
    } catch (error) {
      // Not allowed here (no user gesture, cross-origin frame, already open): native behaviour stays
    }
  }

  refreshDateMinimum() {
    if (!this.dateField) return;
    const today = RushRule.pacificCalendarDate();
    const earliest = RushRule.addDays(today, RushRule.RUSH_MIN_DAYS);
    if (this.dateField.min !== earliest) {
      this.dateField.min = earliest;
    }
    this.dateField.dataset.submittedOn = today;
    return { today, earliest };
  }

  setDateHelper(state) {
    if (!this.dateHelper) return;
    const text = state === 'rush'
      ? RushCopy.helperRush
      : state === 'standard'
        ? RushCopy.helperStandard
        : RushCopy.helperDefault;
    this.dateHelper.textContent = text;
    if (state === 'rush' || state === 'standard') {
      this.dateHelper.dataset.state = state;
    } else {
      delete this.dateHelper.dataset.state;
    }
  }

  writeRushFields(values) {
    Object.keys(this.rushFields).forEach((name) => {
      const input = this.rushFields[name];
      if (Object.prototype.hasOwnProperty.call(values, name)) {
        input.value = values[name];
        input.disabled = false;
      } else {
        input.value = '';
        input.disabled = true;
      }
    });
  }

  clearRushFields() {
    this.writeRushFields({});
  }

  clearRushState() {
    this.rushDecision = null;
    this.clearRushFields();
    this.setDateHelper('default');
  }

  recordStandardChoice(verdict) {
    this.rushDecision = {
      requestedDate: verdict.requestedDate,
      submittedOn: verdict.submittedOn,
      rushRequested: false
    };
    this.writeRushFields({
      rush_requested: 'no',
      rush_fee: RushRule.NO_FEE,
      standard_date: verdict.standardDate,
      submitted_on: verdict.submittedOn,
      rush_rule_version: verdict.ruleVersion
    });
    this.setDateHelper('standard');
  }

  recordRushChoice(verdict) {
    this.rushDecision = {
      requestedDate: verdict.requestedDate,
      submittedOn: verdict.submittedOn,
      rushRequested: true
    };
    this.writeRushFields({
      rush_requested: 'yes',
      rush_fee: RushRule.RUSH_FEE,
      standard_date: verdict.standardDate,
      submitted_on: verdict.submittedOn,
      rush_rule_version: verdict.ruleVersion
    });
    this.setDateHelper('rush');
  }

  /**
   * Classifies the date the field holds right now against today's Pacific day.
   * Standard dates are recorded silently; rush and refused dates open the pop-up
   * so the customer decides before anything else happens.
   * Returns true when the field holds a decided date (or no date at all).
   */
  classifyDateChoice() {
    if (!this.dateField) return true;
    const { today } = this.refreshDateMinimum();
    const value = this.dateField.value;

    if (!value) {
      this.clearRushState();
      return true;
    }

    if (!RushRule.isYmd(value)) {
      // Let the browser's own validity message handle a malformed value
      this.clearRushState();
      return true;
    }

    const verdict = RushRule.classifyRequestedDate(value, today);

    if (verdict.tier === 'standard') {
      this.recordStandardChoice(verdict);
      return true;
    }

    if (verdict.tier === 'rush') {
      const decision = this.rushDecision;
      const decided = decision
        && decision.rushRequested
        && decision.requestedDate === value
        && decision.submittedOn === today;
      if (decided) {
        // Same date, same day, already agreed: keep the fields fresh and move on
        this.recordRushChoice(verdict);
        return true;
      }
    }

    this.rushDecision = null;
    this.clearRushFields();
    this.setDateHelper('default');
    this.openDateModal(verdict);
    return false;
  }

  cancelDateChoice() {
    this.dateField.value = '';
    this.clearRushState();
  }

  closeDateModal(returnFocus) {
    if (!this.dateModal) return;
    const modal = this.dateModal;
    this.dateModal = null;
    document.removeEventListener('keydown', modal.keydownHandler, true);
    modal.remove();
    if (returnFocus && this.dateField) {
      this.dateField.focus();
    }
  }

  openDateModal(verdict) {
    this.closeDateModal(false);

    const standardDate = RushRule.formatDateInWords(verdict.standardDate);
    const earliestDate = RushRule.formatDateInWords(verdict.earliestDate);
    const requestedDate = RushRule.formatDateInWords(verdict.requestedDate);
    const isRefused = verdict.tier === 'refused';

    const modal = document.createElement('div');
    modal.className = 'request-date-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.dataset.tier = verdict.tier;

    const card = document.createElement('div');
    card.className = 'request-date-modal__card';

    const titleId = `request-date-modal-title-${Date.now()}`;
    const title = document.createElement('h3');
    title.className = 'request-date-modal__title';
    title.id = titleId;
    title.textContent = RushCopy.title;
    modal.setAttribute('aria-labelledby', titleId);

    const eyebrow = document.createElement('p');
    eyebrow.className = 'request-date-modal__eyebrow';
    eyebrow.textContent = RushCopy.eyebrow;

    const body = document.createElement('p');
    body.className = 'request-date-modal__message';
    const runs = isRefused
      ? RushCopy.refusedBody(requestedDate, earliestDate, standardDate)
      : RushCopy.rushBody(standardDate, requestedDate);
    runs.forEach((run) => {
      if (typeof run === 'string') {
        body.appendChild(document.createTextNode(run));
      } else {
        const strong = document.createElement('strong');
        strong.textContent = run.strong;
        body.appendChild(strong);
      }
    });

    const actions = document.createElement('div');
    actions.className = 'request-date-modal__actions';

    const buildButton = (variant, choice, label) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `request-date-modal__button request-date-modal__button--${variant}`;
      button.dataset.choice = choice;
      button.setAttribute('aria-label', `${label.main}, ${label.sub}`);
      const main = document.createElement('span');
      main.className = 'request-date-modal__button-main';
      main.textContent = label.main;
      const sub = document.createElement('span');
      sub.className = 'request-date-modal__button-sub';
      sub.textContent = label.sub;
      button.appendChild(main);
      button.appendChild(sub);
      return button;
    };

    const primary = buildButton('primary', 'rush', isRefused ? RushCopy.refusedKeep(earliestDate) : RushCopy.rushKeep);
    const secondary = buildButton('secondary', 'standard', RushCopy.useStandard(standardDate));

    primary.addEventListener('click', () => {
      // Keep my date (rush) or the earliest accepted date (refused), with the fee
      const chosenDate = isRefused ? verdict.earliestDate : verdict.requestedDate;
      this.dateField.value = chosenDate;
      this.recordRushChoice(RushRule.classifyRequestedDate(chosenDate, verdict.submittedOn));
      this.closeDateModal(true);
    });

    secondary.addEventListener('click', () => {
      this.dateField.value = verdict.standardDate;
      this.recordStandardChoice(RushRule.classifyRequestedDate(verdict.standardDate, verdict.submittedOn));
      this.closeDateModal(true);
    });

    // Backdrop click and Escape are a cancel: no date, no decision
    modal.addEventListener('click', (event) => {
      if (event.target === modal) {
        this.cancelDateChoice();
        this.closeDateModal(true);
      }
    });

    const focusables = [primary, secondary];
    const keydownHandler = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.cancelDateChoice();
        this.closeDateModal(true);
        return;
      }
      if (event.key === 'Tab') {
        const index = focusables.indexOf(document.activeElement);
        event.preventDefault();
        const next = event.shiftKey
          ? (index <= 0 ? focusables.length - 1 : index - 1)
          : (index === -1 || index === focusables.length - 1 ? 0 : index + 1);
        focusables[next].focus();
      }
    };
    document.addEventListener('keydown', keydownHandler, true);
    modal.keydownHandler = keydownHandler;

    actions.appendChild(primary);
    actions.appendChild(secondary);
    card.appendChild(eyebrow);
    card.appendChild(title);
    card.appendChild(body);
    card.appendChild(actions);
    modal.appendChild(card);
    document.body.appendChild(modal);
    this.dateModal = modal;
    primary.focus();
  }

  getMinimumForPatchType(patchTypeValue) {
    const patchType = (patchTypeValue || '').toLowerCase();
    if (patchType.includes('embroidered')) return 25;
    if (patchType.includes('pvc') || patchType.includes('tpu') || patchType.includes('reflective') || patchType.includes('ir')) return 50;
    if (patchType.includes('leather')) return 10;
    return 1;
  }

  getConfiguredMinimumQuantity() {
    if (!this.quantityField) return 1;
    const configuredMinimum = Number(this.quantityField.dataset.minQuantity || this.quantityField.min || 1);
    return Number.isFinite(configuredMinimum) && configuredMinimum > 0 ? configuredMinimum : 1;
  }

  getActiveQuantityMinimum() {
    if (this.patchTypeField && this.patchTypeField.value) {
      return this.getMinimumForPatchType(this.patchTypeField.value);
    }

    return this.defaultQuantityMinimum;
  }

  updateStaticQuantityGuidance() {
    if (!this.quantityField) return;

    const minimum = this.getActiveQuantityMinimum();
    this.quantityField.min = String(minimum);

    if (!this.quantityField.placeholder) {
      this.quantityField.placeholder = `Min. ${minimum} units`;
    }

    if (this.quantityHint) {
      this.quantityHint.textContent = `Minimum order: ${minimum} units.`;
      this.quantityHint.style.opacity = '1';
    }
  }

  updateQuantityGuidance() {
    if (!this.patchTypeField || !this.quantityField) return;
    const selectedValue = this.patchTypeField.value;

    if (!selectedValue) {
      this.quantityField.disabled = true;
      this.quantityField.placeholder = 'Quantity Depends on Patch Type';
      if (this.quantityHint) {
        this.quantityHint.textContent = 'Quantity Depends on Patch Type';
        this.quantityHint.style.opacity = '0.5';
      }
      return;
    }

    const minimum = this.getMinimumForPatchType(selectedValue);
    this.quantityField.disabled = false;
    this.quantityField.min = String(minimum);
    this.quantityField.placeholder = `Min. ${minimum} units`;
    if (this.quantityHint) {
      this.quantityHint.textContent = `Minimum order for ${selectedValue}: ${minimum} units`;
      this.quantityHint.style.opacity = '1';
    }
  }

  enforceQuantityMinimum() {
    if (!this.quantityField) return;
    const minimum = this.getActiveQuantityMinimum();
    const quantity = Number(this.quantityField.value || 0);
    if (Number.isFinite(quantity) && quantity > 0 && quantity < minimum) {
      this.quantityField.value = String(minimum);
      if (this.quantityHint) {
        const label = this.patchTypeField && this.patchTypeField.value
          ? `Minimum for ${this.patchTypeField.value}: ${minimum}.`
          : `Minimum order: ${minimum} units.`;
        this.quantityHint.textContent = `${label} Quantity adjusted to ${minimum}.`;
      }
    }
  }

  updatePatchSizeGuidance() {
    if (!this.patchSizeField || !this.customSizeFieldWrap || !this.customSizeField) return;
    const isCustom = (this.patchSizeField.value || '').toLowerCase().includes('custom');
    this.customSizeFieldWrap.hidden = !isCustom;
    this.customSizeField.required = isCustom;
    if (!isCustom) {
      this.customSizeField.value = '';
    }
  }

  /**
   * Leather is priced by its finish, so the finish is shown, enabled and
   * required only while Patch Type is Leather in the single-patch form; for
   * any other type (or a matching set) it is cleared and disabled, so it is
   * neither validated nor posted.
   */
  updateLeatherFinishGuidance() {
    if (!this.leatherFinishField || !this.patchTypeField) return;
    const isLeather = !this.isSetMode() && this.patchTypeField.value === 'Leather';
    this.leatherFinishField.hidden = !isLeather;
    this.leatherFinishInputs.forEach((input) => {
      input.disabled = !isLeather;
      input.required = isLeather;
      if (!isLeather) input.checked = false;
    });
    this.updateLeatherFinishValidity();
  }

  /** The browser's own message for an unanswered finish, in the form's words. */
  updateLeatherFinishValidity() {
    const first = this.leatherFinishInputs[0];
    if (!first) return;
    const missing = !first.disabled && !this.leatherFinishInputs.some((input) => input.checked);
    first.setCustomValidity(missing ? (this.leatherFinishField.dataset.leatherFinishMessage || 'Choose a leather finish.') : '');
    if (!missing) this.showLeatherFinishProblem(false);
  }

  showLeatherFinishProblem(show) {
    if (!this.leatherFinishHint) return;
    this.leatherFinishHint.textContent = show
      ? (this.leatherFinishField.dataset.leatherFinishMessage || 'Choose a leather finish.')
      : this.leatherFinishHintText;
    this.leatherFinishHint.style.color = show ? '#c33' : '';
  }

  /** Re-derives every field whose state follows another field's value. */
  syncDependentFields() {
    if (this.patchTypeField && !this.isSetMode()) this.updateQuantityGuidance();
    this.updatePatchSizeGuidance();
    this.updateLeatherFinishGuidance();
  }

  /**
   * The last check on what actually leaves the browser: a field marked
   * data-must-post that the customer can see must carry a value in the
   * outgoing data. A disabled control is skipped by the browser's own
   * validation and left out of the post, which is how three live requests
   * arrived with no quantity. Returns the first missing control, or null.
   */
  missingMustPostField(formData) {
    const seen = new Set();
    for (const control of this.form.querySelectorAll('[data-must-post]')) {
      if (!control.name || seen.has(control.name)) continue;
      seen.add(control.name);
      if (control.closest('[hidden]')) continue;
      const value = formData.get(control.name);
      if (typeof value === 'string' && value.trim() !== '') continue;
      return control;
    }
    return null;
  }

  mustPostLabel(control) {
    if (control.dataset.mustPost) return control.dataset.mustPost;
    const label = control.closest('label');
    const title = label && label.querySelector('span');
    return title ? title.textContent.trim() : control.name;
  }

  initFileUploadGroups() {
    this.form.querySelectorAll('[data-file-upload-group]').forEach((group) => {
      const slots = Array.from(group.querySelectorAll('[data-file-upload-slot]'));
      const addButton = group.querySelector('[data-add-file-upload]');
      if (!slots.length || !addButton) return;

      // The group's own contract, read from the markup before anything toggles it:
      // a group whose first input is required needs at least one file to submit.
      const firstGroupInput = group.querySelector('input[type="file"]');
      group.dataset.fileRequired = firstGroupInput && firstGroupInput.hasAttribute('required') ? 'yes' : 'no';

      const updateButtonState = () => {
        addButton.hidden = !slots.some((slot) => slot.hidden);
        this.updateFileUploadGroupRequirement(group);
      };

      addButton.addEventListener('click', () => {
        const nextSlot = slots.find((slot) => slot.hidden);
        if (!nextSlot) return;

        nextSlot.hidden = false;
        const nextInput = nextSlot.querySelector('input[type="file"]');
        if (nextInput) nextInput.focus();
        updateButtonState();
      });

      slots.forEach((slot, index) => {
        const input = slot.querySelector('input[type="file"]');
        const removeButton = slot.querySelector('[data-remove-file-upload]');
        if (!input || !removeButton) return;

        removeButton.addEventListener('click', () => {
          input.value = '';
          this.selectedFilesByInput.set(input, []);
          this.clearFileInfo(input);

          if (index > 0) {
            slot.hidden = true;
          }

          this.updateFileUploadGroupRequirement(group);
          updateButtonState();
          this.refreshFileState();
        });
      });

      updateButtonState();
    });
  }

  updateFileUploadGroupRequirement(group) {
    const slots = Array.from(group.querySelectorAll('[data-file-upload-slot]'));
    const inputs = Array.from(group.querySelectorAll('input[type="file"]'));
    const firstInput = inputs[0];
    if (!firstInput) return;

    const inputHasSelectedFile = (input) => {
      const trackedFiles = this.selectedFilesByInput.get(input);
      return (trackedFiles && trackedFiles.length) || (input.files && input.files.length);
    };
    const hasSelectedFile = inputs.some((input) => inputHasSelectedFile(input));

    inputs.forEach((input) => {
      input.required = false;
    });

    firstInput.required = !hasSelectedFile;

    slots.forEach((slot, index) => {
      const input = slot.querySelector('input[type="file"]');
      const removeButton = slot.querySelector('[data-remove-file-upload]');
      if (!input || !removeButton) return;

      removeButton.hidden = slot.hidden || (index === 0 && !inputHasSelectedFile(input));
    });
  }

  /**
   * Every path out of a file selection - accepted, replaced, cancelled, or
   * refused for its type, its size, or the request budget - recomputes the
   * budget from the files the request actually holds (go-live recheck
   * 2026-09-30: a refused replacement cleared the file but left the counter
   * and the Add action describing the file that was no longer there).
   */
  handleFileSelect(event) {
    try {
      this.applyFileSelection(event);
    } finally {
      this.refreshFileState();
    }
  }

  /**
   * After any change to what the request holds, both things derived from it are
   * recomputed from the files actually held: which file inputs are required
   * (go-live recheck 2026-09-30: a refused replacement of an upload group's only
   * file cleared it but left the group "satisfied", so the form could post with
   * no artwork) and the upload budget. Every form that uses this script passes
   * through here.
   */
  refreshFileState() {
    this.refreshRequiredFileState();
    this.refreshUploadBudgetState();
  }

  refreshRequiredFileState() {
    this.form.querySelectorAll('[data-file-upload-group]').forEach((group) => {
      this.updateFileUploadGroupRequirement(group);
    });
  }

  /**
   * The mandatory-file contract checked from the files actually held, whatever
   * the inputs' attributes say: a required upload group with no file in any slot,
   * or a required single input with no file. Returns { message, input } or null.
   */
  requiredFilesProblem() {
    for (const group of Array.from(this.form.querySelectorAll('[data-file-upload-group]'))) {
      if (group.dataset.fileRequired !== 'yes') continue;
      const inputs = Array.from(group.querySelectorAll('input[type="file"]')).filter((input) => !input.disabled);
      if (!inputs.length) continue;
      if (!inputs.some((input) => this.filesOf(input).length)) {
        return { message: 'Please attach at least one file before submitting.', input: inputs[0] };
      }
    }
    for (const input of this.fileInputs) {
      if (input.disabled || !input.isConnected || input.closest('[data-file-upload-group]')) continue;
      if (input.required && !this.filesOf(input).length) {
        const label = input.closest('label');
        const title = label && label.querySelector('span') ? label.querySelector('span').textContent.trim() : 'this field';
        return { message: `Please choose a file for "${title}" before submitting.`, input };
      }
    }
    return null;
  }

  applyFileSelection(event) {
    const input = event.target;
    const newFiles = Array.from(input.files || []);
    if (!newFiles.length) {
      const existingFiles = this.selectedFilesByInput.get(input) || [];
      if (existingFiles.length) {
        this.syncFileInput(input, existingFiles);
        this.showFileInfo(input, existingFiles);
        const uploadGroup = input.closest('[data-file-upload-group]');
        if (uploadGroup) {
          this.updateFileUploadGroupRequirement(uploadGroup);
        }
      } else {
        this.clearFileInfo(input);
      }
      return;
    }

    const files = this.mergeSelectedFiles(input, newFiles);
    if (!files.length) {
      this.clearFileInfo(input);
      return;
    }

    const maxFiles = Number(input.dataset.maxFiles || 0);
    if (maxFiles > 0 && files.length > maxFiles) {
      this.showError(`Please upload no more than ${maxFiles} files.`);
      input.value = '';
      this.selectedFilesByInput.set(input, []);
      this.clearFileInfo(input);
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    const oversizedFile = files.find((file) => file.size > maxSize);
    if (oversizedFile) {
      this.showError(`${oversizedFile.name} exceeds the 10MB limit. Please choose a smaller file.`);
      input.value = '';
      this.selectedFilesByInput.set(input, []);
      this.clearFileInfo(input);
      return;
    }

    const invalidFile = files.find((file) => !this.isAcceptedFileType(input, file));
    if (invalidFile) {
      this.showError(`${invalidFile.name} is not an accepted file type. Please upload one of the accepted types: ${input.accept || 'allowed formats only'}.`);
      input.value = '';
      this.selectedFilesByInput.set(input, []);
      this.clearFileInfo(input);
      return;
    }

    // The request-wide budget: every file in this request counts, not just this input.
    const budgetProblem = this.uploadBudgetProblem({ input, files });
    if (budgetProblem) {
      this.showError(budgetProblem);
      input.value = '';
      this.selectedFilesByInput.set(input, []);
      this.clearFileInfo(input);
      return;
    }

    this.syncFileInput(input, files);
    this.showFileInfo(input, files);
    const uploadGroup = input.closest('[data-file-upload-group]');
    if (uploadGroup) {
      this.updateFileUploadGroupRequirement(uploadGroup);
    }
  }

  // ---------------------------------------------------------------------------
  // Upload budget: one request may carry at most UploadBudget.MAX_FILES files and
  // UploadBudget.MAX_TOTAL_BYTES of file content. Checked when a file is chosen,
  // when a patch card is added or becomes a name tag, and again before posting.
  // ---------------------------------------------------------------------------

  /** Files the request would post right now: enabled inputs only, tracked selection first. */
  filesOf(input) {
    const tracked = this.selectedFilesByInput.get(input);
    return tracked && tracked.length ? tracked : Array.from(input.files || []);
  }

  uploadTotals({ excluding = null } = {}) {
    let count = 0;
    let bytes = 0;
    this.fileInputs.forEach((input) => {
      if (input === excluding || input.disabled || !input.isConnected) return;
      this.filesOf(input).forEach((file) => {
        count += 1;
        bytes += file.size || 0;
      });
    });
    return { count, bytes };
  }

  formatMegabytes(bytes) {
    return `${(bytes / (1000 * 1000)).toFixed(1)} MB`;
  }

  /**
   * Why the request cannot carry these files, or null. With `input`/`files`
   * it answers for a selection being made; without, for the request as it stands.
   */
  uploadBudgetProblem({ input = null, files = [] } = {}) {
    const others = this.uploadTotals({ excluding: input });
    const adding = files.reduce((total, file) => total + (file.size || 0), 0);
    const count = others.count + files.length;
    const bytes = others.bytes + adding;
    const limitMb = this.formatMegabytes(UploadBudget.MAX_TOTAL_BYTES);

    // A multi-patch order also reserves a slot for every required file not chosen yet.
    if (input && this.isSetMode() && input.closest('[data-set-card]')) {
      const slots = this.setFileSlotsUsed({ assumeChosen: input });
      if (slots > UploadBudget.MAX_FILES) {
        return `One request can upload up to ${UploadBudget.MAX_FILES} files, and this order already needs ${UploadBudget.MAX_FILES} for its patches. Leave this file out, remove a patch, or send it as a second request.`;
      }
    }
    if (count > UploadBudget.MAX_FILES) {
      return `One request can upload up to ${UploadBudget.MAX_FILES} files, and this would be ${count}. Remove a file or send the rest as a second request.`;
    }
    if (bytes > UploadBudget.MAX_TOTAL_BYTES) {
      const named = files[0] && files[0].name ? `${files[0].name} would bring this request to` : 'These files total';
      return `${named} ${this.formatMegabytes(bytes)}. One request can carry up to ${limitMb} of files. Use smaller files, or send some patches as a second request.`;
    }
    return null;
  }

  /**
   * THE one recompute of everything the upload budget drives: the patch
   * counter, whether Add another patch is offered, the name-tag extras, and
   * the slow-connection advisory. It reads only the files and cards the
   * request holds right now, and it runs after every add, remove, replace,
   * refusal, and mode or kind change, so the form can never describe a file
   * that is no longer there. Nothing blocks here.
   */
  refreshUploadBudgetState() {
    if (this.setCards) this.syncSetCards();
    const warning = this.form.querySelector('[data-upload-warning]');
    if (!warning) return;
    const { bytes } = this.uploadTotals();
    if (this.isSetMode() && bytes > UploadBudget.SLOW_WARNING_BYTES) {
      warning.textContent = `These files total ${this.formatMegabytes(bytes)}. Our form service allows ${UploadBudget.DEADLINE_SECONDS} seconds per upload, so on a slower connection this may not go through. Files under 5 MB each upload reliably.`;
      warning.hidden = false;
    } else {
      warning.textContent = '';
      warning.hidden = true;
    }
  }

  mergeSelectedFiles(input, newFiles) {
    if (!input.multiple) return newFiles;

    const existingFiles = this.selectedFilesByInput.get(input) || [];
    const filesBySignature = new Map();

    existingFiles.concat(newFiles).forEach((file) => {
      const signature = `${file.name}:${file.size}:${file.lastModified}`;
      filesBySignature.set(signature, file);
    });

    return Array.from(filesBySignature.values());
  }

  syncFileInput(input, files) {
    this.selectedFilesByInput.set(input, files);

    if (!input.multiple || typeof DataTransfer === 'undefined') return;

    const dataTransfer = new DataTransfer();
    files.forEach((file) => dataTransfer.items.add(file));
    input.files = dataTransfer.files;
  }

  isAcceptedFileType(input, file) {
    const accept = (input.getAttribute('accept') || '').trim();
    if (!accept) return true;
    const acceptedPatterns = accept.split(',').map((item) => item.trim().toLowerCase()).filter(Boolean);
    if (!acceptedPatterns.length) return true;

    const fileName = (file.name || '').toLowerCase();
    const mimeType = (file.type || '').toLowerCase();

    return acceptedPatterns.some((pattern) => {
      if (pattern.startsWith('.')) {
        return fileName.endsWith(pattern);
      }
      if (pattern.endsWith('/*')) {
        const category = pattern.slice(0, -1);
        return mimeType.startsWith(category);
      }
      return mimeType === pattern;
    });
  }

  clearFileInfo(input) {
    if (!input || !input.parentElement) return;
    const existingInfo = input.parentElement.querySelector('.file-upload-info');
    if (existingInfo) {
      existingInfo.remove();
    }
  }

  showFileInfo(input, files) {
    if (!input || !input.parentElement) return;
    // Remove existing file info for this input
    const existingInfo = input.parentElement.querySelector('.file-upload-info');
    if (existingInfo) {
      existingInfo.remove();
    }

    const selectedFiles = Array.isArray(files) ? files : [files].filter(Boolean);
    if (!selectedFiles.length) return;

    // Create file info element without injecting user-controlled HTML
    const fileInfo = document.createElement('div');
    fileInfo.className = 'file-upload-info';
    const infoText = document.createElement('p');
    infoText.style.marginTop = '8px';
    infoText.style.fontSize = '12px';
    infoText.style.color = '#666';
    infoText.textContent = selectedFiles.length === 1
      ? `Selected: ${selectedFiles[0].name} (${this.formatFileSize(selectedFiles[0].size)})`
      : `Selected ${selectedFiles.length} files: ${selectedFiles.map((file) => `${file.name} (${this.formatFileSize(file.size)})`).join(', ')}`;
    fileInfo.appendChild(infoText);
    
    input.parentElement.appendChild(fileInfo);
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  async handleSubmit(event) {
    event.preventDefault();

    // Re-run the date rule against today's Pacific day so a tab left open across
    // midnight, a restored value, or a bypassed picker never submits an undecided
    // rush date; the pop-up reopens and nothing is sent.
    if (!this.classifyDateChoice()) {
      return;
    }
    
    if (!this.validateForm()) {
      return;
    }

    if (!this.validateFormAction()) {
      return;
    }

    // Never post a request the form service would refuse: the whole upload is
    // checked once more, whatever path the files took to get here.
    const budgetProblem = this.uploadBudgetProblem();
    if (budgetProblem) {
      this.showError(budgetProblem);
      return;
    }
    const uploadBytes = this.uploadTotals().bytes;

    const formData = this.buildFormData();
    const missingField = this.missingMustPostField(formData);
    if (missingField) {
      this.showError(`Please complete "${this.mustPostLabel(missingField)}" before submitting.`);
      if (missingField.disabled) this.syncDependentFields();
      if (typeof missingField.focus === 'function') missingField.focus();
      return;
    }

    // Store original button text
    const originalText = this.submitButton.textContent || this.submitButton.value;
    
    // Disable submit button and show loading
    if (this.submitButton) {
      this.submitButton.disabled = true;
      if (this.submitButton.tagName === 'BUTTON') {
        this.submitButton.textContent = 'Submitting...';
      } else {
        this.submitButton.value = 'Submitting...';
      }
    }

    try {
      const response = await fetch(this.form.action, {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        this.showSuccess('Your custom request has been submitted successfully! We\'ll contact you soon.');
        this.form.reset();
        this.fileInputs.forEach((input) => {
          this.selectedFilesByInput.set(input, []);
        });
        this.form.querySelectorAll('[data-file-upload-group]').forEach((group) => {
          const slots = Array.from(group.querySelectorAll('[data-file-upload-slot]'));
          slots.forEach((slot, index) => {
            if (index > 0) slot.hidden = true;
          });
          this.updateFileUploadGroupRequirement(group);
        });
        
        // Remove all file info helpers
        this.form.querySelectorAll('.file-upload-info').forEach((el) => el.remove());
      } else {
        // Try to parse error response, but handle non-JSON responses gracefully
        let errorMessage = 'Form submission failed. Please try again.';
        try {
          const text = await response.text();
          if (text) {
            const data = JSON.parse(text);
            if (data.errors && Array.isArray(data.errors)) {
              errorMessage = data.errors.map(err => err.message).join(', ');
            } else if (data.error) {
              errorMessage = data.error;
            }
          }
        } catch (e) {
          // Response wasn't JSON, use default error message
          // Ignore parse errors and use default message
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      // A dropped connection with a large upload is most often the form service's
      // 30-second deadline; say what to change instead of a bare network error.
      const networkFailure = error instanceof TypeError;
      if (networkFailure && uploadBytes > UploadBudget.SLOW_WARNING_BYTES) {
        this.showError(`The upload did not finish. These files total ${this.formatMegabytes(uploadBytes)} and our form service allows ${UploadBudget.DEADLINE_SECONDS} seconds per upload. Try smaller files, a faster connection, or send some patches as a second request.`);
      } else {
        this.showError(error.message || 'An error occurred. Please try again or contact support.');
      }
    } finally {
      // Re-enable submit button
      if (this.submitButton) {
        this.submitButton.disabled = false;
        if (this.submitButton.tagName === 'BUTTON') {
          this.submitButton.textContent = originalText;
        } else {
          this.submitButton.value = originalText;
        }
      }
    }
  }

  buildFormData() {
    const formData = new FormData(this.form);
    const fileFieldNames = new Set(this.fileInputs.map((input) => input.name).filter(Boolean));

    fileFieldNames.forEach((fieldName) => {
      formData.delete(fieldName);
    });

    // The question's own radio is a control, not a field the shop reads.
    formData.delete('set_mode');

    this.fileInputs.forEach((input) => {
      if (input.disabled) return;
      const trackedFiles = this.selectedFilesByInput.get(input);
      const files = trackedFiles && trackedFiles.length
        ? trackedFiles
        : Array.from(input.files || []);
      if (!input.name || !files.length) return;

      files.forEach((file) => {
        if (!file || !file.name) return;
        formData.append(input.name, file, file.name);
      });
    });

    return formData;
  }

  validateForm() {
    // Clear previous error states
    this.form.querySelectorAll('.error').forEach(el => el.classList.remove('error'));

    // Fields that follow another field's value are derived again before the
    // browser validates, so a restored value cannot leave one locked.
    this.syncDependentFields();

    // Honeypot field should remain empty
    const honeypotField = this.form.querySelector('input[name="_gotcha"]');
    if (honeypotField && honeypotField.value) {
      return false;
    }

    // Required-file flags follow the files actually held before the browser validates.
    this.refreshRequiredFileState();

    // Use native required/type validation messaging first
    if (!this.form.checkValidity()) {
      this.form.reportValidity();
      return false;
    }

    // The mandatory files, checked from what the request actually holds: no form
    // may post without the files its own fields require.
    this.refreshRequiredFileState();
    const missingFile = this.requiredFilesProblem();
    if (missingFile) {
      this.showError(missingFile.message);
      if (missingFile.input && typeof missingFile.input.focus === 'function') missingFile.input.focus();
      return false;
    }

    if (!this.validateSetCards()) {
      return false;
    }

    // Validate quantity minimums based on selected patch type
    const patchTypeField = this.patchTypeField;
    const quantityField = this.quantityField;
    if (quantityField) {
      const quantity = Number(quantityField.value || 0);
      const minimum = this.getActiveQuantityMinimum();

      if (!Number.isFinite(quantity) || quantity < minimum) {
        quantityField.value = String(minimum);
        if (this.quantityHint) {
          const label = patchTypeField && patchTypeField.value
            ? `Minimum for ${patchTypeField.value}: ${minimum}.`
            : `Minimum order: ${minimum} units.`;
          this.quantityHint.textContent = `${label} Quantity adjusted to ${minimum}.`;
        }
      }
    }

    // Check required fields (you can customize which fields are required)
    const emailField = this.form.querySelector('input[type="email"]');
    if (emailField && emailField.value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailField.value)) {
        emailField.classList.add('error');
        this.showError('Please enter a valid email address.');
        emailField.focus();
        return false;
      }
    }

    return true;
  }

  validateFormAction() {
    const action = this.form.getAttribute('action') || '';
    if (!action || action === '#') {
      this.showError('Form endpoint is not configured. Please contact support.');
      return false;
    }

    let parsedUrl;
    try {
      parsedUrl = new URL(action, window.location.origin);
    } catch {
      this.showError('Form endpoint is invalid. Please contact support.');
      return false;
    }

    if (parsedUrl.protocol !== 'https:') {
      this.showError('Form endpoint must use HTTPS.');
      return false;
    }

    const isAllowedHost =
      parsedUrl.hostname === this.allowedFormHostSuffix
      || parsedUrl.hostname.endsWith(`.${this.allowedFormHostSuffix}`);
    if (!isAllowedHost) {
      this.showError('Form endpoint is not allowlisted. Please contact support.');
      return false;
    }

    return true;
  }

  showError(message) {
    this.showMessage(message, 'error');
  }

  showSuccess(message) {
    this.showSuccessModal(message, 3);
  }

  ensureSuccessModalStyles() {
    if (document.getElementById('request-success-modal-styles')) return;

    const style = document.createElement('style');
    style.id = 'request-success-modal-styles';
    style.textContent = `
      .request-success-modal {
        position: fixed;
        inset: 0;
        z-index: 9999;
        display: grid;
        place-items: center;
        background: rgba(17, 17, 17, 0.48);
        opacity: 0;
        visibility: hidden;
        transition: opacity 160ms ease, visibility 160ms ease;
      }

      .request-success-modal.is-visible {
        opacity: 1;
        visibility: visible;
      }

      .request-success-modal__card {
        width: min(92vw, 460px);
        background: #fff;
        border: 1px solid #dbd2c4;
        border-radius: 12px;
        padding: 20px 18px;
        box-shadow: 0 20px 50px rgba(0, 0, 0, 0.24);
      }

      .request-success-modal__title {
        margin: 0 0 8px;
        font-family: 'Oswald', system-ui, sans-serif;
        font-size: 28px;
        line-height: 1.1;
        color: #111;
      }

      .request-success-modal__message {
        margin: 0 0 12px;
        font-size: 15px;
        line-height: 1.5;
        color: #222;
      }

      .request-success-modal__countdown {
        margin: 0;
        font-size: 13px;
        line-height: 1.4;
        color: #666;
      }
    `;
    document.head.appendChild(style);
  }

  clearSuccessModalTimers() {
    if (this.successCountdownInterval) {
      clearInterval(this.successCountdownInterval);
      this.successCountdownInterval = null;
    }
    if (this.successCloseTimeout) {
      clearTimeout(this.successCloseTimeout);
      this.successCloseTimeout = null;
    }
  }

  closeSuccessModal() {
    this.clearSuccessModalTimers();
    const modal = document.querySelector('.request-success-modal');
    if (!modal) return;
    modal.classList.remove('is-visible');
    setTimeout(() => {
      modal.remove();
    }, 170);
  }

  showSuccessModal(message, countdownSeconds) {
    this.ensureSuccessModalStyles();
    this.closeSuccessModal();

    const modal = document.createElement('div');
    modal.className = 'request-success-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Form submission successful');

    const card = document.createElement('div');
    card.className = 'request-success-modal__card';

    const title = document.createElement('h3');
    title.className = 'request-success-modal__title';
    title.textContent = 'Request Submitted';

    const body = document.createElement('p');
    body.className = 'request-success-modal__message';
    body.textContent = message;

    const countdown = document.createElement('p');
    countdown.className = 'request-success-modal__countdown';

    card.appendChild(title);
    card.appendChild(body);
    card.appendChild(countdown);
    modal.appendChild(card);
    document.body.appendChild(modal);

    modal.addEventListener('click', (event) => {
      if (event.target === modal) {
        this.closeSuccessModal();
      }
    });

    requestAnimationFrame(() => {
      modal.classList.add('is-visible');
    });

    let remaining = countdownSeconds;
    countdown.textContent = `Closing in ${remaining}...`;

    this.successCountdownInterval = setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        countdown.textContent = 'Closing...';
      } else {
        countdown.textContent = `Closing in ${remaining}...`;
      }
    }, 1000);

    this.successCloseTimeout = setTimeout(() => {
      this.closeSuccessModal();
    }, countdownSeconds * 1000);
  }

  showMessage(message, type) {
    // Remove existing messages
    const existingMessage = this.form.querySelector('.form-message');
    if (existingMessage) {
      existingMessage.remove();
    }

    // Create message element
    const messageEl = document.createElement('div');
    messageEl.className = `form-message form-message--${type}`;
    messageEl.style.cssText = `
      margin-top: 16px;
      padding: 12px 16px;
      border-radius: 4px;
      font-size: 14px;
      ${type === 'error' 
        ? 'background-color: #fee; color: #c33; border: 1px solid #fcc;' 
        : 'background-color: #efe; color: #2a7d2a; border: 1px solid #cfc;'
      }
    `;
    messageEl.textContent = message;

    // Insert before submit button or at end of form
    if (this.submitButton && this.submitButton.parentElement) {
      this.submitButton.parentElement.insertBefore(messageEl, this.submitButton);
    } else {
      this.form.appendChild(messageEl);
    }

    // Auto-remove success messages after 5 seconds
    if (type === 'success') {
      setTimeout(() => {
        messageEl.remove();
      }, 5000);
    }

    // Auto-remove error messages after 8 seconds
    if (type === 'error') {
      setTimeout(() => {
        messageEl.remove();
      }, 8000);
    }
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  const forms = document.querySelectorAll('[data-request-form]');
  forms.forEach(form => {
    new RequestFormUpload(form);
  });
});
